import { createGlobalStyle } from 'styled-components';

/**
 * Theme tokens are CSS color *channels* (not full colors), so any usage in
 * components like `rgb(var(--fg) / 0.78)` automatically flips when the
 * channels are redefined on [data-theme="light"]. This means we don't have
 * to rewrite every styled-component to read theme props — the existing
 * rgba() literals just become rgb(var(--token) / alpha).
 *
 * Channels:
 *   --bg    page background channels
 *   --fg    foreground/text channels (white in dark, near-black in light)
 *   --gray  the muted slate gray used for secondary text + accents
 *
 * Surfaces & borders also derive from --fg: a low-alpha use of --fg
 * gives a subtle white tint on dark or a subtle dark tint on light, which
 * is exactly the behaviour we want for glassmorphism.
 */

const GlobalStyles = createGlobalStyle`
  :root {
    /* dark = default */
    --bg: 2 4 8;
    --fg: 255 255 255;
    --gray: 157 165 174;

    /* Hero light-rays colours (used as full hex/rgb props) */
    --rays-a: #45526d;
    --rays-b: #6b788b;
    --rays-strength: 1;

    /* Shadow alpha multiplier — 1 in dark, much smaller in light so the
       same box-shadow rules don't produce heavy "halo" rings on white. */
    --shadow-mul: 1;

    /* Accent colors that don't flip (semantic) */
    --accent-green: #4ade80;
    --accent-blue:  #60a5fa;
    --accent-purple:#a78bfa;
    --accent-cyan:  #22d3ee;
    --accent-yellow:#facc15;
    --accent-pink:  #f472b6;

    color-scheme: dark;
  }

  [data-theme="light"] {
    --bg: 246 247 249;
    --fg: 10 12 16;
    --gray: 95 105 120;

    --rays-a: #c5ccd4;
    --rays-b: #dde1e6;
    --rays-strength: 0;

    --shadow-mul: 0.3;

    color-scheme: light;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: 'Arial', sans-serif;
  }

  html, body {
    background-color: rgb(var(--bg));
    color: rgb(var(--fg) / 0.92);
    transition: background-color 0.4s ease, color 0.4s ease;
  }

  body {
    padding-top: 4.5rem;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  /* Honor reduced motion for theme transitions too */
  @media (prefers-reduced-motion: reduce) {
    html, body { transition: none; }
  }
`;

export default GlobalStyles;
